import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

const WEBHOOK_URL = 'https://n8n.rvcarbon.com.br/webhook/65ea11b5-aa2a-7f43-135b-b4903558ad1c/chat';

type ChatEvent = { type: string; content?: string };
type ChatPermission = {
    action: string;
    subject: string;
    inverted?: boolean;
};

const getCurrentEnvironment = () => {
    if (typeof window === 'undefined') return '';
    return window.location.origin;
};

const normalizePermissions = (permissions: ChatPermission[]) =>
    permissions.map(({ action, subject, inverted }) => ({
        action,
        subject,
        ...(typeof inverted === 'boolean' ? { inverted } : {}),
    }));


async function streamChat(
    {
        sessionId,
        chatInput,
        permissions,
    }: { sessionId: string; chatInput: string; permissions: ChatPermission[] },
    onChunk: (content: string) => void
) {
    const normalizedPermissions = normalizePermissions(permissions);

    const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            sessionId,
            action: 'sendMessage',
            chatInput,
            permissions: normalizedPermissions,
            ambiente_atual: getCurrentEnvironment(),
        }),
    });

    if (!response.ok || !response.body) {
        throw new Error(`Erro no webhook: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const handleLine = (line: string) => {
        const normalizedLine = line.replace(/^\s*data:\s?/, '').trim();
        if (!normalizedLine) return;

        try {
            const event: ChatEvent = JSON.parse(normalizedLine);

            if (event.type === 'item' && event.content) {
                onChunk(event.content);
            }
        } catch (e) {
            console.warn(e)
        }
    };

    while (true) {
        const { value, done } = await reader.read();

        if (done) {
            break;
        }

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
            handleLine(line);
        }
    }

    buffer += decoder.decode();

    if (buffer.trim()) {
        handleLine(buffer);
    }
}

export function useChatStream(sessionId: string, permissions: ChatPermission[] = []) {
    const [output, setOutput] = useState('');

    const mutation = useMutation({
        mutationFn: (chatInput: string) => {
            setOutput('');
            return streamChat({ sessionId, chatInput, permissions }, (chunk) =>
                setOutput((prev) => prev + chunk)
            );
        },
    });

    return {
        output,
        sendMessage: mutation.mutate,
        isStreaming: mutation.isPending,
        error: mutation.error,
    };
}
